import { Flag01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import type { CrmObject, EntityTypeName } from '@/lib/crm/core/types';
import { useDealStageMove } from '@/lib/crm/pipeline/hooks/useDealStageMove';
import { getStageEntityId } from '@/lib/crm/utils/propertyHelpers';
import { logger } from '@/lib/logger';

import { ACTION_BUTTON_CLASS } from './HeaderActions';
import {
  DEAL_CHANGE_PIPELINE_KEY,
  DEAL_MOVE_STAGE_KEY,
  useDealStageSubmenus,
} from './useDealStageSubmenus';
import { useRecordActions } from './useRecordActions';

type RecordActionMenuBaseProps = {
  object: CrmObject;
  entityTypeName: EntityTypeName;
  /** When true, lifecycle stage items are omitted from the menu entirely. */
  hideLifecycleStage?: boolean;
};

type RecordActionMenuProps =
  | (RecordActionMenuBaseProps & {
      viewContext: 'slideout';
      listRoute?: never;
    })
  | (RecordActionMenuBaseProps & {
      viewContext: 'fullPage';
      listRoute: string;
    });

const DESTRUCTIVE_ITEM_CLASS = '[&_svg]:text-error-600 [&_span]:text-error-600';

/**
 * Kebab overflow menu for a CRM record. Core action logic lives in
 * `useRecordActions` (shared with the slideout header's ResponsiveActionBar);
 * the deal stage/pipeline submenus come from `useDealStageSubmenus`, shared
 * with the slideout's `ResponsiveActionBar`. Only the contact lifecycle
 * submenu is still built inline here.
 */
export function RecordActionMenu({
  object,
  entityTypeName,
  viewContext,
  listRoute,
  hideLifecycleStage,
}: RecordActionMenuProps) {
  const { simpleActions, stageGroups, deleteModal, mergeElements, hasActions } =
    useRecordActions({
      object,
      entityTypeName,
      viewContext,
      listRoute,
      hideLifecycleStage,
    });

  const user = useUser();
  const isContact = entityTypeName === 'ahoy_contact';

  // Contact lifecycle stage — set or change via the same generic stage-move
  // path deals use (optimistic update + conflict retry + rollback). The list of
  // options comes from useRecordActions.stageGroups.
  const currentLifecycleStageId = isContact ? getStageEntityId(object) : null;
  const moveLifecycleStage = useDealStageMove({
    orgId: object.org_id,
    userId: user?.id,
    associationTypeName: 'ahoy_contact_to_lifecycle_stage',
  });
  const [isSettingLifecycleStage, setIsSettingLifecycleStage] = useState(false);

  const handleSetLifecycleStage = useCallback(
    async (stageEntityId: string) => {
      if (
        stageEntityId === currentLifecycleStageId ||
        isSettingLifecycleStage
      ) {
        return;
      }
      setIsSettingLifecycleStage(true);
      try {
        await moveLifecycleStage(object, stageEntityId);
        toast.success('Lifecycle stage updated');
      } catch (error) {
        logger.error(
          'Failed to set lifecycle stage',
          error instanceof Error ? error : new Error(String(error)),
          {
            component: 'crm',
            entity_id: object.entity_id,
            org_id: object.org_id,
          }
        );
        toast.error("We couldn't update the lifecycle stage. Try again.");
      } finally {
        setIsSettingLifecycleStage(false);
      }
    },
    [
      currentLifecycleStageId,
      isSettingLifecycleStage,
      moveLifecycleStage,
      object,
    ]
  );

  const showLifecycleStage = isContact && stageGroups.length > 0;

  const dealSubmenus = useDealStageSubmenus({ object, entityTypeName });
  const moveStageAction = dealSubmenus.find(a => a.key === DEAL_MOVE_STAGE_KEY);
  const changePipelineAction = dealSubmenus.find(
    a => a.key === DEAL_CHANGE_PIPELINE_KEY
  );

  // No empty kebab: a restricted seat with no chat feature and no write grants
  // has nothing to pick from here.
  if (!hasActions && dealSubmenus.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown.Root>
        {/* slideout box styling */}
        <Dropdown.DotsButton
          aria-label='Record actions'
          className={
            viewContext === 'slideout'
              ? 'p-2 hover:bg-[var(--color-bg-tertiary)] rounded-lg transition-colors cursor-pointer text-fg-quaternary'
              : // Full-page view: reuse `ACTION_BUTTON_CLASS` (p-2 box, hover
                // fill) and keep the DropdownDotsButton-default 20px
                // `DotsVertical` icon (no size override) so the kebab matches
                // the slideout ("sidebar") kebab + close at the same 20px size.
                ACTION_BUTTON_CLASS
          }
        />
        <Dropdown.Popover placement='bottom end'>
          <Dropdown.Menu>
            {/* Lifecycle stage — set or change the contact's lifecycle stage.
                A submenu (like the deal "Move stage") rather than a flat dump
                of every stage into the top of the menu. */}
            {showLifecycleStage && (
              <Dropdown.SubmenuTrigger label='Lifecycle stage' icon={Flag01}>
                <Dropdown.Popover>
                  <Dropdown.Menu
                    onAction={key => handleSetLifecycleStage(key as string)}
                    selectedKeys={
                      currentLifecycleStageId
                        ? new Set([currentLifecycleStageId])
                        : new Set<string>()
                    }
                  >
                    {stageGroups.length > 1
                      ? stageGroups.map(group => (
                          <Dropdown.Section key={group.name}>
                            <Dropdown.SectionHeader className='px-4 py-1.5 text-xs font-semibold text-tertiary uppercase tracking-wider'>
                              {group.name === 'default'
                                ? 'Lifecycle stages'
                                : group.name}
                            </Dropdown.SectionHeader>
                            {group.stages.map(stage => (
                              <Dropdown.Item
                                key={stage.entity_id}
                                id={stage.entity_id}
                                textValue={stage.label}
                                isDisabled={isSettingLifecycleStage}
                              >
                                {stage.label}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Section>
                        ))
                      : stageGroups[0]?.stages.map(stage => (
                          <Dropdown.Item
                            key={stage.entity_id}
                            id={stage.entity_id}
                            textValue={stage.label}
                            isDisabled={isSettingLifecycleStage}
                          >
                            {stage.label}
                          </Dropdown.Item>
                        ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.SubmenuTrigger>
            )}

            {moveStageAction && (
              <Dropdown.SubmenuTrigger
                label={moveStageAction.label}
                icon={moveStageAction.icon}
              >
                <Dropdown.Popover>
                  <Dropdown.Menu
                    onAction={key => moveStageAction.onSelect(String(key))}
                    selectedKeys={
                      moveStageAction.selectedKey
                        ? new Set([moveStageAction.selectedKey])
                        : new Set<string>()
                    }
                  >
                    {moveStageAction.options.map(option => (
                      <Dropdown.Item
                        key={option.key}
                        id={option.key}
                        textValue={option.label}
                        isDisabled={option.isDisabled}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.SubmenuTrigger>
            )}

            {changePipelineAction && (
              <Dropdown.SubmenuTrigger
                label={changePipelineAction.label}
                icon={changePipelineAction.icon}
              >
                <Dropdown.Popover>
                  <Dropdown.Menu
                    onAction={key => changePipelineAction.onSelect(String(key))}
                    selectedKeys={
                      changePipelineAction.selectedKey
                        ? new Set([changePipelineAction.selectedKey])
                        : new Set<string>()
                    }
                  >
                    {changePipelineAction.options.map(option => (
                      <Dropdown.Item
                        key={option.key}
                        id={option.key}
                        textValue={option.label}
                        isDisabled={option.isDisabled}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.SubmenuTrigger>
            )}

            {/* Flat actions: Research, Enrich, Chat about, Delete (last, destructive) */}
            {simpleActions.map(action => (
              <Dropdown.Item
                key={action.key}
                onAction={action.onClick}
                isDisabled={action.isDisabled}
                icon={action.icon}
                className={
                  action.destructive ? DESTRUCTIVE_ITEM_CLASS : undefined
                }
              >
                {action.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      {deleteModal}
      {mergeElements}
    </>
  );
}
